import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDesignSystem } from "../../lib/design/designSystem";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import clsx from "clsx";
import CalendarIcon from "../icons/CalendarIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";

/**
 * GLASS DATE PICKER COMPONENT
 *
 * Unified date picker component implementing the glass morphism design system
 * Consolidates Calendar.tsx and DatePicker.tsx functionality
 *
 * Features:
 * - Quick month selection via clickable month header
 * - Quick year selection via clickable year header
 * - "Today" quick action button
 * - Year range navigation for distant dates
 * - Portal rendering to avoid clipping in modals
 */

type ViewMode = "days" | "months" | "years";

interface GlassDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  position?: "up" | "down";
}

const GlassDatePicker: React.FC<GlassDatePickerProps> = ({
  value,
  onChange,
  id,
  label,
  placeholder,
  disabled = false,
  error,
  helperText,
  className,
  position = "down",
}) => {
  const { surfaces, typography } = useDesignSystem();
  const { t } = useTranslation("common");
  const { locale } = useLocaleFormat();

  const resolvedPlaceholder = placeholder ?? t("date_placeholder");

  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("days");
  const [yearRangeStart, setYearRangeStart] = useState(
    Math.floor(new Date().getFullYear() / 12) * 12
  );
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedDate = useMemo(
    () => (value ? new Date(`${value}T12:00:00`) : null),
    [value]
  );
  const [displayDate, setDisplayDate] = useState(selectedDate || new Date());

  // Derive locale-aware month and day names
  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { month: "long" }).format(
          new Date(2000, i, 1)
        )
      ),
    [locale]
  );

  const monthNamesShort = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { month: "short" }).format(
          new Date(2000, i, 1)
        )
      ),
    [locale]
  );

  // Day names Mon–Sun (2024-01-01 is a Monday)
  const dayNames = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(
          new Date(2024, 0, 1 + i)
        )
      ),
    [locale]
  );

  const formattedDate = useMemo(() => {
    if (!value) return "";
    try {
      const date = new Date(`${value}T12:00:00`);
      return date.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return "";
    }
  }, [value, locale]);

  // Reset view mode when picker opens
  useEffect(() => {
    if (isOpen) {
      setViewMode("days");
      setYearRangeStart(Math.floor(displayDate.getFullYear() / 12) * 12);
      updatePosition();
    }
  }, [isOpen]);

  const updatePosition = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      // Default to "down"
      let top = rect.bottom + scrollTop + 8;

      // If position is "up" or if there isn't enough space below (approx 350px)
      const spaceBelow = window.innerHeight - rect.bottom;
      if (position === "up" || (position === "down" && spaceBelow < 350)) {
         // Place above: top of element - height of picker (approx 320px) - margin
         top = rect.top + scrollTop - 330;
      }

      // Align the popup with the input's inline-start edge (popup is w-72 = 288px)
      const isRtl = document.documentElement.dir === "rtl";
      const popupWidth = 288;
      let left = isRtl ? rect.right - popupWidth : rect.left;
      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - popupWidth - 8);
      }

      setCoords({
        top,
        left,
      });
    }
  };

  // Handle scroll/resize to close or update (closing is safer/easier)
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
       // Ideally we'd update position, but closing avoids detached popups
       // allowing updatePosition on scroll if we want it to stick:
       updatePosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, position]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Check if click is inside wrapper (input)
      const isWrapper = wrapperRef.current && wrapperRef.current.contains(event.target as Node);

      // Check if click is inside portal (we can't easily check ref here since it's in a portal,
      // but we can check if target is inside an element with specific data attribute)
      const target = event.target as HTMLElement;
      const isPortal = target.closest('[data-glass-datepicker-portal]');

      if (!isWrapper && !isPortal) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calendar logic
  const year = displayDate.getFullYear();
  const month = displayDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  const days = useMemo(() => {
    const d = [];
    const startOffset = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    for (let i = 0; i < startOffset; i++) {
      d.push(<div key={`empty-start-${i}`} className="p-1"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);

      const y = currentDate.getFullYear();
      const m = String(currentDate.getMonth() + 1).padStart(2, "0");
      const d_ = String(currentDate.getDate()).padStart(2, "0");
      const dateString = `${y}-${m}-${d_}`;

      const isSelected =
        selectedDate &&
        currentDate.toDateString() === selectedDate.toDateString();
      const isToday = new Date().toDateString() === currentDate.toDateString();

      d.push(
        <button
          key={day}
          onClick={() => {
            onChange(dateString);
            setIsOpen(false);
          }}
          className={clsx(
            "flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors",
            isSelected
              ? "bg-primary-500 text-white font-semibold shadow"
              : isToday
              ? "border border-primary-400/60 text-white"
              : "text-white/80 hover:bg-white/10"
          )}
        >
          {day}
        </button>
      );
    }
    return d;
  }, [year, month, daysInMonth, startingDayOfWeek, selectedDate, onChange]);

  const changeMonth = (delta: number) => {
    setDisplayDate(new Date(year, month + delta, 1));
  };

  const handleMonthSelect = (monthIndex: number) => {
    setDisplayDate(new Date(year, monthIndex, 1));
    setViewMode("days");
  };

  const handleYearSelect = (selectedYear: number) => {
    setDisplayDate(new Date(selectedYear, month, 1));
    setViewMode("months");
  };

  const handleTodayClick = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setDisplayDate(today);
    setIsOpen(false);
  };

  const changeYearRange = (delta: number) => {
    setYearRangeStart(yearRangeStart + delta * 12);
  };

  const years = useMemo(() => {
    const yearsArray = [];
    for (let i = 0; i < 12; i++) {
      yearsArray.push(yearRangeStart + i);
    }
    return yearsArray;
  }, [yearRangeStart]);

  const calendarPopup = (
    <div
      className="absolute z-[9999] w-72"
      style={{ top: coords.top, left: coords.left }}
      data-glass-datepicker-portal
    >
      <div
        className={clsx(
          surfaces.card,
          "w-72 p-4 text-white shadow-[0_25px_80px_-45px_rgba(15,23,42,0.9)] border border-white/10"
        )}
      >
        {/* Years View */}
        {viewMode === "years" && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <button
                onClick={() => changeYearRange(-1)}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 rotate-90 rtl:-rotate-90" />
              </button>
              <div className="text-lg font-semibold cursor-pointer hover:text-primary-400">
                {yearRangeStart} - {yearRangeStart + 11}
              </div>
              <button
                onClick={() => changeYearRange(1)}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 -rotate-90 rtl:rotate-90" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {years.map((y) => {
                const isSelected = y === year;
                const isCurrent = y === new Date().getFullYear();
                return (
                  <button
                    key={y}
                    onClick={() => handleYearSelect(y)}
                    className={clsx(
                      "rounded-lg py-2.5 text-sm font-medium transition-all",
                      isSelected
                        ? "bg-primary-500 text-white shadow-lg"
                        : isCurrent
                        ? "border border-primary-400/60 text-white hover:bg-white/10"
                        : "text-white/80 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Months View */}
        {viewMode === "months" && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <button
                onClick={() => setViewMode("years")}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 rotate-90 rtl:-rotate-90" />
              </button>
              <button
                onClick={() => setViewMode("years")}
                className="text-lg font-semibold hover:text-primary-400 transition-colors"
                title={t("datepicker.change_year")}
              >
                {year}
              </button>
              <button
                onClick={() => setDisplayDate(new Date(year + 1, month, 1))}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 -rotate-90 rtl:rotate-90" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {monthNamesShort.map((m, index) => {
                const isSelected = index === month;
                const isCurrent =
                  index === new Date().getMonth() &&
                  year === new Date().getFullYear();
                return (
                  <button
                    key={m}
                    onClick={() => handleMonthSelect(index)}
                    className={clsx(
                      "rounded-lg py-2.5 text-sm font-medium transition-all",
                      isSelected
                        ? "bg-primary-500 text-white shadow-lg"
                        : isCurrent
                        ? "border border-primary-400/60 text-white hover:bg-white/10"
                        : "text-white/80 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Days View (Default) */}
        {viewMode === "days" && (
          <>
            <div className="mb-2 flex items-center justify-between">
              <button
                onClick={() => changeMonth(-1)}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 rotate-90 rtl:-rotate-90" />
              </button>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewMode("months")}
                  className="text-lg font-semibold hover:text-primary-400 transition-colors px-1 rounded flex items-center gap-1"
                  title={t("datepicker.select_month")}
                >
                  {monthNames[month]}
                </button>
                <button
                  onClick={() => setViewMode("years")}
                  className="text-lg font-semibold hover:text-primary-400 transition-colors px-1 rounded"
                  title={t("datepicker.select_year")}
                >
                  {year}
                </button>
              </div>
              <button
                onClick={() => changeMonth(1)}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <ChevronDownIcon className="h-5 w-5 -rotate-90 rtl:rotate-90" />
              </button>
            </div>

            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs text-white/60">
              {dayNames.map((day, i) => (
                <div key={i}>{day}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 justify-items-center gap-1">
              {days}
            </div>
          </>
        )}

        {/* Today Button */}
        <div className="mt-3 pt-3 border-t border-white/10">
          <button
            onClick={handleTodayClick}
            className="w-full rounded-lg py-2 text-sm font-medium text-primary-400 hover:bg-primary-500/20 transition-colors"
          >
            {t("datepicker.today")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={clsx("space-y-1", className)}>
      {label && <label className={typography.label}>{label}</label>}

      <div className="relative" ref={wrapperRef}>
        <div className="relative">
          <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <CalendarIcon className="h-5 w-5 text-white/40" />
          </div>
          <input
            type="text"
            id={id}
            value={formattedDate}
            onClick={() => {
               if(!disabled) {
                  setIsOpen(!isOpen);
                  if(!isOpen) updatePosition();
               }
            }}
            readOnly
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            className={clsx(
              surfaces.input,
              "ps-10 cursor-pointer",
              disabled && surfaces.inputDisabled,
              error && "border-rose-500/50 focus:border-rose-500/70"
            )}
          />
        </div>

        {isOpen && !disabled && createPortal(calendarPopup, document.body)}
      </div>

      {error && <p className="text-xs text-rose-300">{error}</p>}
      {helperText && !error && <p className={typography.caption}>{helperText}</p>}
    </div>
  );
};

export default GlassDatePicker;
